import React, { useEffect, useRef } from "react";
import { Pin, PinOff } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAppSettings } from "../context/AppSettingsContext";
import { useAuth } from "../context/AuthContext";

const iso = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export const WEEKDAY_LABELS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

// Resolve a custom preset rule to {from, to}. weekdays: 0=Lun..6=Dom within current Mon-Sun week.
export function resolveCustomPreset(p, today = new Date()) {
  if (p.type === "fixed") return { from: p.from || "", to: p.to || "" };
  if (p.type === "last_days") {
    const n = Math.max(1, parseInt(p.days, 10) || 1);
    return { from: iso(addDays(today, -(n - 1))), to: iso(today) };
  }
  if (p.type === "weekdays") {
    const days = (p.weekdays || []).map(Number).filter((x) => x >= 0 && x <= 6).sort((a, b) => a - b);
    if (!days.length) return { from: "", to: "" };
    const dow = (today.getDay() + 6) % 7; // 0=Mon
    const monday = addDays(today, -dow);
    return { from: iso(addDays(monday, days[0])), to: iso(addDays(monday, days[days.length - 1])) };
  }
  return { from: "", to: "" };
}

export function builtinPresets(today = new Date()) {
  const todayIso = iso(today);
  const yIso = iso(addDays(today, -1));
  const firstPrev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastPrev = new Date(today.getFullYear(), today.getMonth(), 0);
  return [
    { k: "today",      label: "Oggi",        from: todayIso,               to: todayIso },
    { k: "yesterday",  label: "Ieri",        from: yIso,                   to: yIso },
    { k: "week",       label: "Settimana",   from: iso(addDays(today, -6)),   to: todayIso },
    { k: "month",      label: "Mese",        from: iso(addDays(today, -29)),  to: todayIso },
    { k: "last_month", label: "Mese scorso", from: iso(firstPrev),         to: iso(lastPrev) },
    { k: "year",       label: "Anno",        from: iso(addDays(today, -364)), to: todayIso },
  ];
}

/**
 * Quick date range preset chips. Built-in + admin custom presets (settings.date_presets).
 * props: from, to (YYYY-MM-DD), onChange({from,to}), testId prefix,
 *        pageKey (optional): enables per-operator "default preset" pin, auto-applied on mount.
 */
export default function DateRangePresets({ from, to, onChange, testId = "date-preset", pageKey }) {
  const ctx = useAppSettings();
  const auth = useAuth();
  const user = auth?.user;
  const customs = (ctx?.settings?.date_presets || []).map((p) => ({
    k: `custom-${p.id}`, label: p.name, custom: true, ...resolveCustomPreset(p),
  }));
  const presets = [...builtinPresets(), ...customs, { k: "custom", label: "Personalizza", from: "", to: "" }];
  const activeKey = presets.find((p) => p.from === from && p.to === to)?.k || null;

  const defaults = user?.preferences?.default_date_presets || {};
  const defaultKey = pageKey ? defaults[pageKey] || null : null;
  const isDefaultActive = !!defaultKey && defaultKey === activeKey;

  // Apply the operator's default preset once on mount (only if no range is set yet)
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current || !pageKey || !user) return;
    applied.current = true;
    if (!defaultKey || from || to) return;
    const p = presets.find((x) => x.k === defaultKey);
    if (p && p.from && p.to) onChange({ from: p.from, to: p.to });
    // eslint-disable-next-line
  }, [pageKey, user, defaultKey]);

  const saveDefault = async (key) => {
    const next = { ...defaults, [pageKey]: key };
    try {
      const { data } = await api.patch("/auth/prefs", { default_date_presets: next });
      auth.setUser({ ...user, preferences: data.preferences });
      toast.success(key ? "Preset predefinito salvato per questa pagina" : "Preset predefinito rimosso");
    } catch { toast.error("Impossibile salvare la preferenza"); }
  };

  return (
    <div className="flex flex-wrap gap-1 items-center" data-testid={testId}>
      {presets.map((p) => {
        const on = activeKey === p.k;
        const isDef = defaultKey === p.k;
        const base = on
          ? "bg-amber-500/20 text-amber-300 border-amber-500/50"
          : p.custom
            ? "border-indigo-800/60 text-indigo-300/80 hover:border-indigo-500 hover:text-indigo-200"
            : "border-zinc-800 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300";
        return (
          <button
            key={p.k}
            type="button"
            onClick={() => onChange({ from: p.from, to: p.to })}
            data-testid={`${testId}-${p.k}`}
            title={p.from && p.to ? `${p.from} → ${p.to}${isDef ? " · predefinito" : ""}` : undefined}
            className={`text-[10px] px-2 py-1 rounded-full border transition ${base}`}
          >
            {isDef ? "📌 " : p.custom ? "★ " : ""}{p.label}
          </button>
        );
      })}
      {pageKey && user && activeKey && activeKey !== "custom" && (
        <button
          type="button"
          data-testid={`${testId}-pin`}
          onClick={() => saveDefault(isDefaultActive ? null : activeKey)}
          title={isDefaultActive ? "Rimuovi preset predefinito" : "Applica questo preset automaticamente all'apertura"}
          className={`text-[10px] px-2 py-1 rounded-full border flex items-center gap-1 transition ${isDefaultActive ? "border-emerald-700/60 text-emerald-300 bg-emerald-500/10" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}
        >
          {isDefaultActive ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
          {isDefaultActive ? "Predefinito" : "Imposta predefinito"}
        </button>
      )}
    </div>
  );
}
